import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'utils/axiosUtil';
import { DateUtil } from 'doctivity-shared/utils';
import {
  Button,
  Typography,
  Chip,
  IconButton,
  Box,
  Icon,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DynamicTable, AnnouncementDialog, AdminToolsTabs } from 'components';

const STATUS_COLORS = {
  Active: 'success',
  Scheduled: 'info',
  Expired: 'default',
  Off: 'warning',
};

const fmtDate = (v) => (v ? DateUtil.formatFromDB(v) : null);

/**
 * Admin history + composer for announcements (Messages of type 'announcement').
 * Self-contained: reads/writes /api/v2/messages directly. Admin-gated in AppRoot.
 */
const AnnouncementsPage = () => {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/v2/messages');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Clients for the audience picker + the user-picker's client filter.
    axiosInstance
      .get('/Clients', { params: { opts: { where: { is_active: true }, order: [['name', 'ASC']] } } })
      .then((res) => setClients(res.data?.rows || res.data || []))
      .catch(() => setClients([]));
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const onRowClick = (row) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this announcement? Recipients will no longer see it.')) return;
    try {
      await axiosInstance.delete(`/v2/messages/${id}`);
      load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert('Could not delete the announcement.');
    }
  };

  const columns = [
    { label: 'Title', key: 'title', sortable: false, filter: false, style: { minWidth: 180 } },
    { label: 'Audience', key: 'audience', sortable: false, filter: false },
    {
      label: 'Status',
      key: 'status',
      sortable: false,
      filter: false,
      format: (v) => <Chip size='small' label={v} color={STATUS_COLORS[v] || 'default'} />,
    },
    {
      label: 'Read',
      key: 'read_count',
      sortable: false,
      filter: false,
      format: (v, row) => (row && row.recipient_count != null ? `${v || 0}/${row.recipient_count}` : ''),
    },
    { label: 'Start', key: 'starts_at', sortable: false, filter: false, format: (v) => fmtDate(v) || '—' },
    { label: 'End', key: 'ends_at', sortable: false, filter: false, format: (v) => fmtDate(v) || 'Never' },
    { label: 'Sender', key: 'sender_name', sortable: false, filter: false },
    {
      label: '',
      key: 'id',
      sortable: false,
      filter: false,
      style: { width: 48 },
      format: (v) => (
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(v);
          }}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      ),
    },
  ];

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <Typography component='h2' variant='h6' color='primary'>
        Announcements
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Button color='primary' size='small' onClick={openCreate}>
        <Icon sx={{ mr: 0.5 }}>add</Icon>
        New Announcement
      </Button>
    </Box>
  );

  return (
    <>
      <AdminToolsTabs tab='announcements' />
      <Box sx={{ p: 2 }}>
      <DynamicTable
        header={header}
        columns={columns}
        idKey='id'
        query={{ limit: 100, offset: 0, order: [['created_at', 'DESC']] }}
        data={{ rows, count: rows.length, loading }}
        onRowClick={onRowClick}
      />
      <AnnouncementDialog
        open={dialogOpen}
        editing={editing}
        clients={clients}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          load();
        }}
      />
      </Box>
    </>
  );
};

export { AnnouncementsPage };
export default AnnouncementsPage;
